import { Component, computed, forwardRef, input, output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QMEncryptDirective } from '../../../directives/quantum-metric.directive';

@Component({
  selector: 'app-text-input',
  standalone: true,
  imports: [FormsModule, QMEncryptDirective],
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TextInputComponent),
    },
  ],
})
export class TextInputComponent implements ControlValueAccessor {
  readonly placeHolder = input('');
  readonly label = input<string | undefined>();
  readonly name = input.required<string>();
  readonly dataTestId = input<string | undefined>();
  readonly tabbable = input(true);
  readonly maxlength = input<number | undefined>();

  error = input<string | undefined>(undefined);
  success = input(false);
  touched = false;
  disabled = false;
  value: string | undefined;
  showKeyboardFocus = false;
  private isUsingKeyboard = false;

  onChange: (value: any) => void = () => {};
  onTouched = () => {};
  inputFocused = output<void>();

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          this.isUsingKeyboard = true;
        }
      });

      document.addEventListener('mousedown', () => {
        this.isUsingKeyboard = false;
      });
    }
  }

  private readonly viewModel = computed(() => ({
    label: this.label(),
    name: this.name(),
    placeHolder: this.placeHolder(),
    error: this.error(),
    success: this.success(),
    dataTestId: this.dataTestId(),
    maxlength: this.maxlength(),
  }));

  get vm() {
    return this.viewModel();
  }

  writeValue(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  updateValue(value: string) {
    this.value = value;
    this.onChange(value);
    this.markAsTouched();
  }

  onInputFocus() {
    this.showKeyboardFocus = this.isUsingKeyboard;
    this.inputFocused.emit();
  }

  onInputBlur() {
    this.showKeyboardFocus = false;
  }

  keyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }
}
